import React, { useState, useEffect, useRef } from "react";
import useAxios from "../../hooks/useAxios";
import { useAuth } from "../../contexts/AuthContext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { ProgressBar } from "primereact/progressbar";
import { Panel } from "primereact/panel";

const KPI_RULES = {
  TCHHR: [
    { min: 75, max: Infinity, cls: "kpi-bad" },
    { min: 50, max: 75, cls: "kpi-warning" },
  ],
  CSSR: [
    { min: 0, max: 95, cls: "kpi-bad" },
    { min: 95, max: 97, cls: "kpi-warning" },
  ],
  TCongR: [
    { min: 15, max: Infinity, cls: "kpi-bad" },
    { min: 10, max: 15, cls: "kpi-warning" },
  ],
  TBusyR: [
    { min: 10, max: Infinity, cls: "kpi-bad" },
    { min: 5, max: 10, cls: "kpi-warning" },
  ],
  TCHUnR: [
    { min: 10, max: Infinity, cls: "kpi-bad" },
    { min: 5, max: 10, cls: "kpi-warning" },
  ],
  TCDR: [
    { min: 3, max: Infinity, cls: "kpi-bad" },
    { min: 2, max: 3, cls: "kpi-warning" },
  ],
  TCDRH: [
    { min: 5, max: Infinity, cls: "kpi-bad" },
    { min: 2, max: 5, cls: "kpi-warning" },
  ],
  IHSR: [
    { min: 0, max: 85, cls: "kpi-bad" },
    { min: 85, max: 90, cls: "kpi-warning" },
  ],
  OHSR: [
    { min: 0, max: 85, cls: "kpi-bad" },
    { min: 85, max: 90, cls: "kpi-warning" },
  ],
  SDBusyR: [
    { min: 2, max: Infinity, cls: "kpi-bad" },
    { min: 1, max: 2, cls: "kpi-warning" },
  ],
  SDCDR: [
    { min: 2, max: Infinity, cls: "kpi-bad" },
    { min: 1, max: 2, cls: "kpi-warning" },
  ],
  GVQC: [
    { min: 0, max: 90, cls: "kpi-bad" },
    { min: 90, max: 95, cls: "kpi-warning" },
  ],
};

const getKpiClass = (field, value) => {
  if (value === null || value === undefined) return "";
  const rules = KPI_RULES[field];
  if (!rules) return "";
  const numVal = parseFloat(value);
  if (isNaN(numVal)) return "";
  for (const rule of rules) {
    if (numVal >= rule.min && numVal < rule.max) return rule.cls;
  }
  return "";
};

const KPI_COLUMNS = [
  "TCHTr", "TCHHR", "CSSR", "TCHSR", "TCongR", "TBusyR",
  "TCHUnR", "TRXUs", "TRX", "TCDR", "TCDRH", "IHSR",
  "OHSR", "SDSR", "SDBusyR", "SDCDR", "GVQC", "AvBwGPRS", "AvBwEDGE",
];

const Stats2G = () => {
  const { user } = useAuth();
  const { response: bscResponse, fetchData: fetchBscData } = useAxios();
  const { response: cellResponse, loading: loadingCells, fetchData: fetchCellData } = useAxios();
  const { response: statsResponse, loading, fetchData: fetchStatsData } = useAxios();

  const [bscList, setBscList] = useState([]);
  const [cellList, setCellList] = useState([]);
  const [selectedBsc, setSelectedBsc] = useState(null);
  const [selectedCells, setSelectedCells] = useState([]);
  const [date1, setDate1] = useState(new Date());
  const [date2, setDate2] = useState(new Date());
  const [reportType, setReportType] = useState("hourly");
  const [tableData, setTableData] = useState([]);

  const toast = useRef(null);
  const dt = useRef(null);

  useEffect(() => {
    fetchBscData({ method: "get", url: "/api/stats-2g/bsc-list" });
  }, []);

  useEffect(() => {
    if (!bscResponse) return;
    if (bscResponse.status === "success" && bscResponse.data) {
      setBscList(bscResponse.data.map((b) => ({ label: b.BSC_Name, value: b.BSC_ID })));
    }
  }, [bscResponse]);

  useEffect(() => {
    if (!cellResponse) return;
    if (cellResponse.status === "success" && cellResponse.data) {
      setCellList(cellResponse.data.map((c) => ({ label: c.Cell_Name, value: c.Cell_ID })));
    }
  }, [cellResponse]);

  useEffect(() => {
    if (!statsResponse) return;
    if (statsResponse.status === "success" && statsResponse.data) {
      setTableData(statsResponse.data);
    } else if (statsResponse.status === "error") {
      toast.current?.show({ severity: "error", detail: statsResponse.message, life: 3000 });
    }
  }, [statsResponse]);

  const handleBscChange = (e) => {
    setSelectedBsc(e.value);
    setSelectedCells([]);
    setCellList([]);
    if (e.value) {
      const bscId = e.value === "all" ? "bsc_all" : e.value;
      fetchCellData({ method: "get", url: "/api/stats-2g/cells", params: { bscId } });
    }
  };

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleSearch = () => {
    if (selectedCells.length === 0) {
      toast.current.show({ severity: "warn", detail: "Выберите сектора", life: 3000 });
      return;
    }
    fetchStatsData({
      method: "post",
      url: "/api/stats-2g",
      data: {
        cellIds: selectedCells,
        date1: formatDate(date1),
        date2: formatDate(date2),
        reportType,
      },
    });
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const kpiBodyTemplate = (field) => (rowData) => {
    const value = rowData[field];
    if (value === null || value === undefined) return "n/a";
    const cls = getKpiClass(field, value);
    const display = typeof value === "number" ? Math.round(value * 10) / 10 : value;
    return <span className={cls}>{display}</span>;
  };

  if (!user) return null;

  return (
    <div className="flex justify-content-center">
      <div style={{ width: "100%", maxWidth: "1600px", padding: "1rem" }}>
        <Toast ref={toast} position="top-right" />

        <Panel header="📶 Параметры отчета 2G" className="mb-4">
          <div className="grid">
            <div className="col-12 md:col-3">
              <label className="block mb-1 font-bold text-sm">Контроллер:</label>
              <Dropdown
                value={selectedBsc}
                options={[{ label: "Все", value: "all" }, ...bscList]}
                onChange={handleBscChange}
                placeholder="Выберите BSC"
                className="w-full p-inputtext-sm"
              />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-1 font-bold text-sm">Сектора:</label>
              <MultiSelect
                value={selectedCells}
                options={cellList}
                onChange={(e) => setSelectedCells(e.value)}
                placeholder="Выберите сектора"
                className="w-full p-inputtext-sm"
                maxSelectedLabels={3}
                filter
                loading={loadingCells}
              />
            </div>
            <div className="col-6 md:col-2">
              <label className="block mb-1 font-bold text-sm">Дата с:</label>
              <Calendar value={date1} onChange={(e) => setDate1(e.value)} dateFormat="yy-mm-dd" className="w-full p-inputtext-sm" showIcon />
            </div>
            <div className="col-6 md:col-2">
              <label className="block mb-1 font-bold text-sm">Дата по:</label>
              <Calendar value={date2} onChange={(e) => setDate2(e.value)} dateFormat="yy-mm-dd" className="w-full p-inputtext-sm" showIcon />
            </div>
            <div className="col-12 md:col-2 flex align-items-end gap-2">
              <Button label="Показать" icon="pi pi-eye" className="p-button-primary p-button-sm" onClick={handleSearch} />
              <Button label="CSV" icon="pi pi-file" className="p-button-success p-button-sm" onClick={exportCSV} disabled={tableData.length === 0} />
            </div>
          </div>
        </Panel>

        {loading ? (
          <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
        ) : tableData.length > 0 ? (
          <DataTable
            ref={dt}
            value={tableData}
            paginator
            rows={100}
            rowsPerPageOptions={[50, 100, 500]}
            size="small"
            showGridlines
            stripedRows
            scrollable
            scrollHeight="600px"
            exportFilename="2g_stats"
            style={{ fontSize: "0.8rem" }}
            emptyMessage="Нет данных"
          >
            <Column field="cell_name" header="Cell" sortable frozen style={{ minWidth: "120px" }} />
            <Column field="data" header="Date" sortable style={{ minWidth: "130px" }} />
            {KPI_COLUMNS.map((col) => (
              <Column key={col} field={col} header={col} sortable body={kpiBodyTemplate(col)} style={{ minWidth: "80px" }} />
            ))}
          </DataTable>
        ) : null}
      </div>

      <style>{`
        .kpi-bad { background-color: #ffcdd2; padding: 2px 6px; border-radius: 3px; }
        .kpi-warning { background-color: #fff9c4; padding: 2px 6px; border-radius: 3px; }
      `}</style>
    </div>
  );
};

export default Stats2G;
